/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { X, Plus, Minus, Trash2, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { PAYMENT_MODE } from '../../constant/constant';

export default function EditOrderModal({ isOpen, onClose, onConfirm, orderDetails, isLoading }) {
  const [editedItems, setEditedItems] = useState([]);
  const [calculations, setCalculations] = useState({
    subtotal: 0,
    deliveryCharges: 0,
    total: 0,
  });

  useEffect(() => {
    if (isOpen && orderDetails) {
      // Initialize edited items from order details
      const items = orderDetails.items.map((item) => ({
        ...item,
        originalQuantity: item.quantity,
      }));
      setEditedItems(items);
      calculateTotals(items);
    }
  }, [isOpen, orderDetails]);

  const calculateTotals = (items) => {
    const subtotal = items.reduce((acc, item) => {
      return acc + (item.netPrice || item.mrp || 0) * (item.quantity || 0);
    }, 0);

    const deliveryCharges =
      orderDetails?.attributeModels?.find((attr) => attr?.name === 'Delivery Charge')?.value || 0;

    const total = subtotal + Number(deliveryCharges);

    setCalculations({
      subtotal,
      deliveryCharges: Number(deliveryCharges),
      total,
    });
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;

    const updated = [...editedItems];
    updated[index].quantity = newQuantity;
    setEditedItems(updated);
    calculateTotals(updated);
  };

  const removeItem = (index) => {
    if (editedItems.length === 1) {
      toast.error('Cannot remove all items. At least one item is required.');
      return;
    }

    const updated = editedItems.filter((_, i) => i !== index);
    setEditedItems(updated);
    calculateTotals(updated);
  };

  const handleSubmit = () => {
    if (editedItems.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }

    const originalTotal = orderDetails?.total || 0;
    const newTotal = calculations.total;
    const priceDifference = newTotal - originalTotal;

    // Check if this is a prepaid order
    const isPrepaid =
      orderDetails?.paymentStatus && orderDetails.paymentStatus !== PAYMENT_MODE.PAY_ON_DELIVERY;

    // Check if order was previously updated
    const wasUpdatedBefore = orderDetails?.attributeModels?.some(
      (attr) => attr.name === 'Order Updated On' && attr.value
    );

    // Prepare attribute models with payment difference tracking
    let updatedAttributeModels = [
      ...orderDetails.attributeModels.map((attr) => ({
        name: attr.name,
        value: attr.value,
      })),
    ];

    // Add or update "Order Updated On" timestamp
    const updateIndex = updatedAttributeModels.findIndex(
      (attr) => attr.name === 'Order Updated On'
    );
    if (updateIndex !== -1) {
      updatedAttributeModels[updateIndex].value = new Date().toISOString();
    } else {
      updatedAttributeModels.push({
        name: 'Order Updated On',
        value: new Date().toISOString(),
      });
    }

    // For prepaid orders, track payment difference
    if (isPrepaid && !wasUpdatedBefore && priceDifference !== 0) {
      // Store the payment difference
      updatedAttributeModels.push({
        name: 'Payment Difference',
        value: priceDifference.toString(),
      });

      updatedAttributeModels.push({
        name: 'Payment Difference Type',
        value: priceDifference > 0 ? 'ADDITIONAL_PAYMENT_REQUIRED' : 'REFUND_DUE',
      });

      updatedAttributeModels.push({
        name: 'Original Order Total',
        value: originalTotal.toString(),
      });

      updatedAttributeModels.push({
        name: 'Updated Order Total',
        value: newTotal.toString(),
      });
    } else if (isPrepaid && wasUpdatedBefore && priceDifference !== 0) {
      // If already updated before, accumulate the difference
      const existingDiff = parseFloat(
        orderDetails.attributeModels.find((attr) => attr.name === 'Payment Difference')?.value ||
          '0'
      );
      const totalDiff = existingDiff + priceDifference;

      const diffIndex = updatedAttributeModels.findIndex(
        (attr) => attr.name === 'Payment Difference'
      );
      if (diffIndex !== -1) {
        updatedAttributeModels[diffIndex].value = totalDiff.toString();
      } else {
        updatedAttributeModels.push({
          name: 'Payment Difference',
          value: totalDiff.toString(),
        });
      }

      const typeIndex = updatedAttributeModels.findIndex(
        (attr) => attr.name === 'Payment Difference Type'
      );
      if (typeIndex !== -1) {
        updatedAttributeModels[typeIndex].value =
          totalDiff > 0 ? 'ADDITIONAL_PAYMENT_REQUIRED' : 'REFUND_DUE';
      } else {
        updatedAttributeModels.push({
          name: 'Payment Difference Type',
          value: totalDiff > 0 ? 'ADDITIONAL_PAYMENT_REQUIRED' : 'REFUND_DUE',
        });
      }

      const updatedTotalIndex = updatedAttributeModels.findIndex(
        (attr) => attr.name === 'Updated Order Total'
      );
      if (updatedTotalIndex !== -1) {
        updatedAttributeModels[updatedTotalIndex].value = newTotal.toString();
      } else {
        updatedAttributeModels.push({
          name: 'Updated Order Total',
          value: newTotal.toString(),
        });
      }
    }

    // Prepare the updated order data
    const updatedOrderData = {
      id: orderDetails.id,
      orderId: orderDetails.orderId,
      mrp: calculations.subtotal,
      discount: 0,
      total: newTotal,
      contactNo: orderDetails.contactNo,
      deliveryAddress: orderDetails.deliveryAddress,
      vendorShopName: orderDetails.vendorShopName,
      vendorContactNo: orderDetails.vendorContactNo,
      vendorAddress: orderDetails.vendorAddress,
      status: orderDetails.status,
      note: orderDetails.note,
      items: editedItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        productImageUrls: item.productImageUrls || [],
        quantity: item.quantity,
        variant: item.variant,
        variantId: item.variantId,
        unit: item.unit,
        mrp: item.mrp,
        netPrice: item.netPrice,
      })),
      attributeModels: updatedAttributeModels,
      paymentOrderId: orderDetails.paymentOrderId || '',
      paymentStatus: orderDetails.paymentStatus || '',
      _paymentDifference: priceDifference, // Pass to parent for toast message
      _isPrepaid: isPrepaid,
    };

    onConfirm(updatedOrderData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Edit Order</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {editedItems.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <img
                  src={item.productImageUrls?.[0] || '/images/imagenotfound.webp'}
                  alt={item.productName}
                  onError={(e) => {
                    e.currentTarget.src = '/images/imagenotfound.webp';
                  }}
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                    {item.productName}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {item.variant} {item.unit} • ₹{item.netPrice || item.mrp}
                  </p>

                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-2 border border-gray-300 rounded-lg">
                      <button
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        className="p-1.5 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="size-4" />
                      </button>
                      <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="p-1.5 hover:bg-gray-100"
                      >
                        <Plus className="size-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(index)}
                      disabled={editedItems.length === 1}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove item"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="font-semibold text-gray-900 text-sm">
                    ₹{(item.netPrice || item.mrp || 0) * item.quantity}
                  </p>
                  {item.quantity !== item.originalQuantity && (
                    <p className="text-xs text-orange-600 mt-1">Modified</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Payment Difference Warning */}
          {orderDetails?.paymentStatus &&
            orderDetails.paymentStatus !== PAYMENT_MODE.PAY_ON_DELIVERY &&
            calculations.total !== orderDetails.total && (
              <div
                className={`mt-4 p-4 rounded-lg border-2 ${
                  calculations.total < orderDetails.total
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-full ${
                      calculations.total < orderDetails.total ? 'bg-blue-100' : 'bg-orange-100'
                    }`}
                  >
                    {calculations.total < orderDetails.total ? (
                      <TrendingDown className="size-5 text-blue-600" />
                    ) : (
                      <TrendingUp className="size-5 text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle
                        className={`size-4 ${
                          calculations.total < orderDetails.total
                            ? 'text-blue-600'
                            : 'text-orange-600'
                        }`}
                      />
                      <h4
                        className={`font-semibold text-sm ${
                          calculations.total < orderDetails.total
                            ? 'text-blue-900'
                            : 'text-orange-900'
                        }`}
                      >
                        {calculations.total < orderDetails.total
                          ? 'Refund Due to Customer'
                          : 'Additional Payment Required'}
                      </h4>
                    </div>
                    <p
                      className={`text-xs ${
                        calculations.total < orderDetails.total
                          ? 'text-blue-700'
                          : 'text-orange-700'
                      }`}
                    >
                      This is a prepaid order. The price difference will be tracked.
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Difference:</span>
                      <span
                        className={`font-bold ${
                          calculations.total < orderDetails.total
                            ? 'text-blue-900'
                            : 'text-orange-900'
                        }`}
                      >
                        ₹{Math.abs(calculations.total - orderDetails.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal ({editedItems.length} items)</span>
              <span className="font-medium">₹{calculations.subtotal}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Delivery Charges</span>
              <span className="font-medium">₹{calculations.deliveryCharges}</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between text-base font-semibold">
                <span>Total</span>
                <span>₹{calculations.total}</span>
              </div>
              {orderDetails?.total && calculations.total !== orderDetails.total && (
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Original Total</span>
                  <span>₹{orderDetails.total}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isLoading ? 'Updating...' : 'Update Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
